import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { ProductType } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useDeleteProductType } from '../hooks/use-product-type-mutations';

export interface ProductTypeDangerZoneProps {
  productType: ProductType;
  /** Called once the backend has deleted the type, to leave its page. */
  onDeleted: () => void;
}

/** The backend refuses (409 RESOURCE_IN_USE) while a product still has this type. */
export function ProductTypeDangerZone({ productType, onDeleted }: ProductTypeDangerZoneProps) {
  const { t } = useTranslation(['productTypes', 'common', 'errors']);
  const remove = useDeleteProductType();
  const message = useErrorMessage();
  const error = isApiError(remove.error) ? remove.error : null;

  return (
    <FormSection title={t('productTypes:danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('productTypes:danger.description')}</p>
      {remove.isError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {error?.code === 'RESOURCE_IN_USE'
              ? t('productTypes:danger.inUse')
              : message(remove.error)}
            {error?.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="text-destructive hover:text-destructive w-full"
            disabled={remove.isPending}
          >
            <Trash2 aria-hidden />
            {t('productTypes:danger.delete')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('productTypes:danger.confirmTitle', { name: productType.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('productTypes:danger.confirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(productType.id, { onSuccess: onDeleted })}
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormSection>
  );
}
